import type {
  DropDownNoSelection,
  DropDownResult,
  DynamicFormField,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import { OdinDataSender, isDropDownNoSelection } from '@myosh/odin-components';
import { cloneDeep } from 'lodash';
import type { DropDownValue } from '../@types/common';
import type { CombinedFieldType, ExtendedDynamicFormSettings, ExtendedDynamicFormWorkflowStep } from '../@types/forms';
import type {
  FieldOptionsApiProps,
  LinkedFieldOptionsApiProps,
  LinkedPersonOptionApiProps,
  PersonOption,
  PersonOptionApiProps,
} from '../@types/options';
import type { TrainingDropDownValue } from '../@types/training-field';
import { findFieldRecursiveSearch } from '../components/record/util/record-component-util';
import { api } from '../redux/services/api';
import { fieldApi } from '../redux/services/field';
import { hierarchyApi } from '../redux/services/hierarchy';
import { moduleApi } from '../redux/services/module';
import { appDispatch } from '../redux/store-accessors';
import { extractFieldName } from './data-grid-column-utils';
import { dataProps } from './dynamic-form-utils';
import { gridCheckboxFilterOptions } from './dynamic-page-utils';

interface FilterWorkflowStep {
  id: number | string;
  label: string;
}

interface CustomFilterProperties {
  selectedForm?: DropDownResult | DropDownNoSelection;
  workflowSteps?: Array<FilterWorkflowStep>;
}

export const getDataGridComboboxFilters = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  isAllForms: boolean,
  customFilterProperties?: CustomFilterProperties,
  formSettings?: ExtendedDynamicFormSettings,
  moduleId?: number
) => {
  switch (options.customProperties?.entityType) {
    case 'FIELD':
      if (isAllForms) {
        await getAllFormsFieldKeyFilters(subscriber, options);
      } else {
        await getFieldKeyFilters(subscriber, options, customFilterProperties);
      }
      break;
    case 'KEYWORD':
      await getKeywordKeyFilter(subscriber, options, customFilterProperties, moduleId);
      break;
    case 'HIERARCHY_TYPE':
      await getHierarchyKeyFilter(subscriber, options);
      break;
    case 'RECORD_LINK':
    case 'REVERSE_RECORD_LINK':
      await getRecordLinkKeyFilter(subscriber, options, customFilterProperties, formSettings);
      break;
    default:
      subscriber.sendData();
  }
};

const getFieldKeyFilters = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  customFilterProperties?: CustomFilterProperties
) => {
  if (
    getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'FIELD_OPTIONS' &&
    (options.getAllData ||
      (options.page && (options.page === 1 || (options.page > 1 && options.customProperties?.next))))
  ) {
    const formId = isDropDownNoSelection(customFilterProperties?.selectedForm)
      ? customFilterProperties?.selectedForm.noSelectionValue
      : customFilterProperties?.selectedForm?.value;
    return getFieldOptions(subscriber, options, formId as number);
  } else if (
    getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'PERSON_OPTIONS' &&
    (options.getAllData ||
      (options.page && (options.page === 1 || (options.page > 1 && options.customProperties?.next))))
  ) {
    return getPersonFieldOptions(subscriber, options);
  } else if (getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'CHECKBOX_OPTIONS') {
    if (options.page === 1 || options.getAllData) {
      subscriber.sendData({ data: gridCheckboxFilterOptions, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
  } else if (
    getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'COMPETENCIES_OPTIONS'
  ) {
    return getTrainingFieldOptions(subscriber, options);
  } else {
    subscriber.sendData();
  }
};

const getAllFormsFieldKeyFilters = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions
) => {
  const pageSize = options.getAllData ? -1 : options.pageSize || 50;
  if (
    getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'FIELD_OPTIONS' &&
    options.page
  ) {
    modifyOptionsArchivedFilter(options);
    const optionDispatch = appDispatch(
      fieldApi.endpoints.allFormsFieldOptions.initiate({
        queryId: options.customProperties?.entityId as number,
        filters: options.fieldFilters,
        pageSize,
        page: options.page,
      })
    );
    const optionResult = await optionDispatch;
    if (optionResult.status === 'fulfilled' && optionResult.data.items && optionResult.data.items.length > 0) {
      const data = optionResult.data.items.map((item) => {
        return dataProps.archivedField
          ? {
              [dataProps.valueField]: item.id,
              [dataProps.textField]: item.includeArchived ? addArchivedSuffix(item.caption) : item.caption,
              captionValue: item.caption,
              [dataProps.archivedField]: item.includeArchived,
            }
          : {
              [dataProps.valueField]: item.id,
              [dataProps.textField]: item.includeArchived ? addArchivedSuffix(item.caption) : item.caption,
              captionValue: item.caption,
            };
      });

      subscriber.sendData({
        data,
        requestId: options.requestId,
      });
      if (data.length < pageSize || optionResult.data.page === optionResult.data.availablePages) {
        subscriber.sendData();
      }
    } else {
      subscriber.sendData();
    }

    optionDispatch.unsubscribe();
  } else if (
    getOptionsByFieldType(options.customProperties?.fieldType as CombinedFieldType) === 'PERSON_OPTIONS' &&
    options.page
  ) {
    if (options?.fieldFilters?.caption) {
      options.fieldFilters['name'] = options.fieldFilters.caption;
      delete options.fieldFilters.caption;
    }
    modifyOptionsArchivedFilter(options);
    const personDispatch = appDispatch(
      fieldApi.endpoints.allFormsPersonFieldOptions.initiate({
        queryId: (options.customProperties?.entityId || 0) as number,
        pageSize,
        page: options.page,
        filters: options.fieldFilters,
      })
    );
    const personResult = await personDispatch;
    if (personResult.status === 'fulfilled' && personResult.data.items && personResult.data.items?.length > 0) {
      const personValues = personResult.data.items.map((el: DropDownValue) => {
        return dataProps.archivedField
          ? {
              [dataProps.valueField]: el.id,
              [dataProps.textField]: el.archived ? addArchivedSuffix(el.value) : el.value,
              captionValue: el.value,
              [dataProps.archivedField]: el.archived,
            }
          : {
              [dataProps.valueField]: el.id,
              [dataProps.textField]: el.archived ? addArchivedSuffix(el.value) : el.value,
              captionValue: el.value,
            };
      });
      subscriber.sendData({
        data: personValues,
        requestId: options.requestId,
      });

      if (options.getAllData || personValues.length < pageSize) {
        subscriber.sendData();
      }
    } else {
      subscriber.sendData();
    }
    personDispatch.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

const getKeywordKeyFilter = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  customFilterProperties?: CustomFilterProperties,
  moduleId?: number
) => {
  if (
    options.customProperties?.field === 'status' &&
    customFilterProperties?.workflowSteps &&
    (options.page === 1 || options.getAllData)
  ) {
    const workflowValues = customFilterProperties.workflowSteps
      .filter((workflowStep) =>
        workflowStep.label
          .toLowerCase()
          .includes(options.fieldFilters ? options.fieldFilters['caption'].value.toString().toLowerCase() : '')
      )
      .map((workflowStep) => {
        return {
          [dataProps.valueField]: workflowStep.id,
          [dataProps.textField]: workflowStep.label,
          captionValue: workflowStep.label,
        };
      });
    subscriber.sendData({ data: workflowValues, requestId: options.requestId });
  } else if (options.customProperties?.field === 'accountable' && (options.getAllData || options.page)) {
    const formId = isDropDownNoSelection(customFilterProperties?.selectedForm)
      ? customFilterProperties?.selectedForm.noSelectionValue
      : customFilterProperties?.selectedForm?.value;

    return getKeywordAccountableOptions(subscriber, options, formId as number);
  } else if (
    options.customProperties?.field === 'moduleForm' &&
    moduleId &&
    (options.getAllData || options.page === 1)
  ) {
    const modulesDispatch = appDispatch(moduleApi.endpoints.modules.initiate({ includeFormList: true }));

    const modulesResult = await modulesDispatch;

    if (modulesResult.status === 'fulfilled' && modulesResult.data.length > 0) {
      const module = modulesResult.data.find((module) => module.id === moduleId);
      if (module) {
        let forms = module.forms ?? [];
        const filter = (options.fieldFilters?.['caption']?.value as string)?.toLowerCase() ?? undefined;
        if (filter) {
          forms = forms.filter((forms) => forms.caption.toLowerCase().includes(filter));
        }
        const _forms = forms.map((form) => {
          return {
            [dataProps.valueField]: form.id,
            [dataProps.textField]: form.caption,
          };
        });

        subscriber.sendData({ data: _forms, requestId: options.requestId });
      } else {
        subscriber.sendData();
      }
    } else {
      subscriber.sendData();
    }

    modulesDispatch.unsubscribe();
  } else if (options.customProperties?.field === 'author' && (options.page || options.getAllData)) {
    const formId = isDropDownNoSelection(customFilterProperties?.selectedForm)
      ? customFilterProperties?.selectedForm.noSelectionValue
      : customFilterProperties?.selectedForm?.value;
    return getAuthorOptions(subscriber, options, formId as number);
  } else {
    subscriber.sendData();
  }
};

export const getHierarchyKeyFilter = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  typeId?: number // if `typeId` specified, it's used for fetching the hierarchies, otherwise the `entityId` is used
) => {
  if (options && options.customProperties) {
    modifyOptionsArchivedFilter(options);

    const filters = cloneDeep(options.fieldFilters ?? {});
    filters!['typeId'] = { value: typeId ?? Number(options.customProperties.entityId), comparison: '=' };

    const hierarchyDispatch = appDispatch(
      hierarchyApi.endpoints.hierarchyValues.initiate({
        page: options?.page ?? 1,
        pageSize: options.getAllData ? -1 : (options?.pageSize ?? 50),
        sort: 'caption',
        filters,
      })
    );
    const hierarchyResponse = await hierarchyDispatch;
    if (hierarchyResponse.data && hierarchyResponse.status === 'fulfilled' && hierarchyResponse.data?.items) {
      const hierarchyItems = hierarchyResponse.data?.items.map((item) => {
        return {
          ...item,
          [dataProps.textField]: item.archived ? addArchivedSuffix(item.caption) : item.caption,
          captionValue: item.caption,
        };
      });

      subscriber.sendData({ data: hierarchyItems, requestId: options.requestId });

      if (hierarchyResponse.data.page === hierarchyResponse.data.availablePages) {
        subscriber.sendData();
      }
    } else {
      subscriber.sendData();
    }

    hierarchyDispatch.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

const getRecordLinkKeyFilter = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  customFilterProperties?: CustomFilterProperties,
  formSettings?: ExtendedDynamicFormSettings
) => {
  if (options?.customProperties?.linkedEntityType === 'HIERARCHY_TYPE') {
    return getHierarchyKeyFilter(subscriber, options, Number(options.customProperties.linkedFieldId));
  } else if (options?.customProperties?.linkedEntityType === 'KEYWORD' && formSettings) {
    const keywordField = extractFieldName(options.customProperties?.field as string);

    if (
      options.customProperties &&
      keywordField.toLowerCase() === 'status' &&
      (options.page === 1 || options.getAllData)
    ) {
      let statusOptionsDispatch,
        isAllModules = false;

      const targetFormId = getTargetFormId(formSettings.fields, options?.customProperties?.entityId);
      if (targetFormId) {
        modifyOptionsArchivedFilter(options);
        statusOptionsDispatch = appDispatch(
          api.endpoints.getWorkflowSteps.initiate({
            formId: targetFormId,
            filters: { archived: { value: false, comparison: '=' }, ...options.fieldFilters },
          })
        );
      } else if (!targetFormId && options?.customProperties?.entityId) {
        // If there is no targetFormId, then the keyword field is most likely configured with 'All Modules',
        //  so we need to use the fieldId to retrieve the options
        isAllModules = true;
        const fieldId = options.customProperties.entityId as number;
        statusOptionsDispatch = appDispatch(
          fieldApi.endpoints.fieldWorkflowSteps.initiate({
            fieldId,
            filters: { archived: { value: false, comparison: '=' }, ...options.fieldFilters },
          })
        );
      }

      if (statusOptionsDispatch) {
        const statusOptions = await statusOptionsDispatch;

        if (statusOptions.status === 'fulfilled' && statusOptions.data.length > 0) {
          const statusValues = statusOptions.data.map((status) => ({
            [dataProps.valueField]: isAllModules === true ? status : (status as ExtendedDynamicFormWorkflowStep).id,
            [dataProps.textField]: isAllModules === true ? status : (status as ExtendedDynamicFormWorkflowStep).label,
          }));

          subscriber.sendData({ data: statusValues, requestId: options.requestId });
        } else {
          subscriber.sendData();
        }

        statusOptionsDispatch.unsubscribe();
      } else {
        subscriber.sendData();
      }
    } else if (options.customProperties && keywordField.toLowerCase() === 'accountable') {
      const targetFormId = getTargetFormId(formSettings.fields, options?.customProperties?.entityId);
      if (targetFormId) {
        return getKeywordAccountableOptions(subscriber, options, targetFormId);
      } else if (!targetFormId && options?.customProperties?.entityId) {
        // If there is no targetFormId, then the keyword field is most likely configured with 'All Modules',
        //  so we need to use the fieldId to retrieve the options
        const fieldId = options.customProperties.entityId as number;
        return getKeywordAccountableOptions(subscriber, options, fieldId, true);
      } else {
        subscriber.sendData();
      }
    } else if (options.customProperties && keywordField.toLowerCase() === 'author') {
      return getAuthorOptions(subscriber, options, undefined, Number(options.customProperties.entityId));
    } else {
      subscriber.sendData();
    }
  } else if (
    options?.customProperties?.linkedEntityType === 'FIELD' &&
    getOptionsByFieldType(options.customProperties?.linkedFieldType as CombinedFieldType) === 'PERSON_OPTIONS' &&
    options.page &&
    (options.page === 1 || (options.page > 1 && options.customProperties?.next))
  ) {
    return getPersonFieldOptions(subscriber, options);
  } else if (
    options?.customProperties?.linkedEntityType === 'FIELD' &&
    getOptionsByFieldType(options?.customProperties?.linkedFieldType as CombinedFieldType) === 'FIELD_OPTIONS' &&
    options.page &&
    (options.page === 1 || options.getAllData === true || (options.page > 1 && options.customProperties?.next)) &&
    formSettings
  ) {
    // Should use the formId of the currently opened module/form
    const formId = isDropDownNoSelection(customFilterProperties?.selectedForm)
      ? customFilterProperties?.selectedForm.noSelectionValue
      : customFilterProperties?.selectedForm?.value;

    return getFieldOptions(subscriber, options, formId as number);
  } else if (
    options?.customProperties?.linkedEntityType === 'FIELD' &&
    getOptionsByFieldType(options.customProperties?.linkedFieldType as CombinedFieldType) === 'CHECKBOX_OPTIONS'
  ) {
    if (options.page === 1 || options.getAllData) {
      subscriber.sendData({ data: gridCheckboxFilterOptions, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
  } else if (
    options?.customProperties?.linkedEntityType === 'FIELD' &&
    getOptionsByFieldType(options?.customProperties?.linkedFieldType as CombinedFieldType) === 'COMPETENCIES_OPTIONS' &&
    options.page
  ) {
    return getTrainingFieldOptions(subscriber, options);
  } else {
    subscriber.sendData();
  }
};

export const getOptionsByFieldType = (fieldType: CombinedFieldType) => {
  switch (fieldType) {
    case 'PERSONFIELD':
    case 'MULTISELECTPERSONFIELD':
    case 'READERS':
    case 'USER_RECORDLINK':
    case 'TWINCOLUMNSELECTPERSONFIELD':
      return 'PERSON_OPTIONS';
    case 'MULTISELECTCHECKBOX':
    case 'OPTIONGROUP':
    case 'COMBOBOX':
    case 'MULTISELECTFIELD':
    case 'TWINCOLUMNSELECT':
      return 'FIELD_OPTIONS';
    case 'TRAINING':
      return 'COMPETENCIES_OPTIONS';
    case 'RECORDLINK':
      return 'RECORD_LINK_OPTIONS';
    case 'CHECKBOX':
      return 'CHECKBOX_OPTIONS';
    default:
      return 'FIELD_OPTIONS';
  }
};

const getFieldOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  formId: number
) => {
  modifyOptionsArchivedFilter(options);

  const queryConfig: FieldOptionsApiProps | LinkedFieldOptionsApiProps = {
    queryId: options.customProperties?.entityId as number,
    limit: options.getAllData ? -1 : options.pageSize || 50,
    filters: options.fieldFilters,
    next: options.customProperties?.next as string,
    linkedFieldId: options?.customProperties?.linkedFieldId
      ? Number(options.customProperties.linkedFieldId)
      : undefined,
    formId,
    formRecordsOnly: hasLookupType(options),
  };

  const optionDispatch = appDispatch(fieldApi.endpoints.fieldOptions.initiate(queryConfig));

  const optionResult = await optionDispatch;
  if (optionResult.status === 'fulfilled' && optionResult.data.items && optionResult.data.items.length > 0) {
    const _options = optionResult.data.items.map((item) => {
      return {
        ...item,
        [dataProps.textField]: item.archived === true ? addArchivedSuffix(item.caption) : item.caption,
        captionValue: item.caption,
      };
    });

    subscriber.sendData({
      data: _options,
      requestId: options.requestId,
      customProperties: { next: optionResult.data?.next },
    });
  } else {
    subscriber.sendData();
  }

  optionDispatch.unsubscribe();
};

const getTrainingFieldOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions
) => {
  if (options?.fieldFilters?.caption) {
    options.fieldFilters['name'] = options.fieldFilters.caption;
    delete options.fieldFilters.caption;
  }

  modifyOptionsArchivedFilter(options);
  const pageSize = options.getAllData ? -1 : options.pageSize || 50;

  const dispatchResult = appDispatch(
    fieldApi.endpoints.trainingFieldOptions.initiate({
      page: options.page || 1,
      pageSize,
      filters: options.fieldFilters,
    })
  );

  const result = await dispatchResult;

  if ('fulfilled' === result.status && result.data && result.data.length > 0) {
    const trainingFieldOptions = result.data?.map((item: TrainingDropDownValue) => {
      return dataProps.archivedField
        ? {
            ...item,
            [dataProps.valueField]: item.id,
            [dataProps.textField]: item.archived ? addArchivedSuffix(item.name) : item.name,
            captionValue: item.name,
            [dataProps.archivedField]: item.archived,
          }
        : {
            ...item,
            [dataProps.valueField]: item.id,
            [dataProps.textField]: item.archived ? addArchivedSuffix(item.name) : item.name,
            captionValue: item.name,
          };
    });

    subscriber.sendData({ data: trainingFieldOptions, requestId: options.requestId });
    if (trainingFieldOptions.length < pageSize) {
      subscriber.sendData();
    }
  } else {
    subscriber.sendData();
  }

  dispatchResult.unsubscribe();
};

const getPersonFieldOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions
) => {
  if (options && options.customProperties) {
    if (options?.fieldFilters?.caption) {
      options.fieldFilters['value'] = options.fieldFilters.caption;
      delete options.fieldFilters.caption;
    }

    modifyOptionsArchivedFilter(options);

    const queryConfig: PersonOptionApiProps | LinkedPersonOptionApiProps = {
      queryId: (options.customProperties?.entityId || 0) as number,
      limit: options.getAllData ? -1 : options.pageSize || 50,
      filters: options.fieldFilters,
      next: options.customProperties?.next as string,
      formRecordsOnly: true,
    };

    if ('linkedFieldId' in options.customProperties) {
      (queryConfig as LinkedPersonOptionApiProps).linkedFieldId = Number(options.customProperties.linkedFieldId);
    }

    const personDispatch = appDispatch(fieldApi.endpoints.personFieldOptions.initiate(queryConfig));

    const personResult = await personDispatch;

    if (personResult.status === 'fulfilled' && personResult.data.items && personResult.data.items?.length > 0) {
      const personValues = personResult.data.items.map((el: PersonOption) => {
        return dataProps.archivedField
          ? {
              [dataProps.valueField]: el.id,
              [dataProps.textField]: el.archived ? addArchivedSuffix(el.value) : el.value,
              captionValue: el.value,
              [dataProps.archivedField]: el.archived,
            }
          : {
              [dataProps.valueField]: el.id,
              [dataProps.textField]: el.archived ? addArchivedSuffix(el.value) : el.value,
              captionValue: el.value,
            };
      });
      subscriber.sendData({
        data: personValues,
        requestId: options.requestId,
        customProperties: { next: personResult.data?.next },
      });
    } else {
      subscriber.sendData();
    }

    personDispatch.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

const getKeywordAccountableOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  queryId: number, // formId or fieldId
  isRecordLink?: boolean // when true, data is fetched via a different endpoint
) => {
  if (options?.fieldFilters?.caption) {
    options.fieldFilters['name'] = options.fieldFilters.caption;
    delete options.fieldFilters.caption;
  }

  modifyOptionsArchivedFilter(options);

  const pageSize = options.getAllData ? -1 : options.pageSize || 50;
  let personDispatch;

  if (isRecordLink === true) {
    personDispatch = appDispatch(
      fieldApi.endpoints.fieldAccountablePersonOptions.initiate({
        queryId,
        pageSize,
        page: options.page ?? 1,
        filters: options.fieldFilters,
      })
    );
  } else {
    personDispatch = appDispatch(
      fieldApi.endpoints.accountablePersonFieldOptions.initiate({
        queryId,
        pageSize,
        page: options.page || -1,
        filters: options.fieldFilters,
      })
    );
  }

  if (personDispatch) {
    const personResult = await personDispatch;

    if (personResult.status === 'fulfilled' && personResult.data.items && personResult.data.items.length > 0) {
      const personValues = personResult.data.items.map((person) => {
        return dataProps.archivedField
          ? {
              [dataProps.valueField]: person.id,
              [dataProps.textField]: person.archived ? addArchivedSuffix(person.value) : person.value,
              captionValue: person.value,
              [dataProps.archivedField]: person.archived,
            }
          : {
              [dataProps.valueField]: person.id,
              [dataProps.textField]: person.archived ? addArchivedSuffix(person.value) : person.value,
              captionValue: person.value,
            };
      });
      subscriber.sendData({ data: personValues, requestId: options.requestId });

      if (personValues.length < pageSize) {
        subscriber.sendData();
      }
    } else {
      subscriber.sendData();
    }

    personDispatch.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

const getAuthorOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  formId?: number,
  linkedFieldId?: number
) => {
  if (options?.fieldFilters?.caption) {
    options.fieldFilters['name'] = options.fieldFilters.caption;
    delete options.fieldFilters.caption;
  }

  modifyOptionsArchivedFilter(options);
  const pageSize = options.getAllData ? -1 : options.pageSize || 50;
  const dispatchResult = appDispatch(
    fieldApi.endpoints.authorOptions.initiate({
      page: options.page || 1,
      pageSize,
      filters: options.fieldFilters,
      formId,
      linkedFieldId,
    })
  );

  const result = await dispatchResult;

  if ('fulfilled' === result.status && result.data && result.data.items && result.data.items.length > 0) {
    const authorFieldOptions = result.data.items.map((item) => {
      return dataProps.archivedField
        ? {
            [dataProps.valueField]: item.id,
            [dataProps.textField]: item.archived ? addArchivedSuffix(item.value) : item.value,
            captionValue: item.value,
            [dataProps.archivedField]: item.archived,
          }
        : {
            [dataProps.valueField]: item.id,
            [dataProps.textField]: item.archived ? addArchivedSuffix(item.value) : item.value,
            captionValue: item.value,
          };
    });

    subscriber.sendData({ data: authorFieldOptions, requestId: options.requestId });
    const isLastPage = result.data.page === result.data.availablePages || options.getAllData;
    if (authorFieldOptions.length < pageSize || isLastPage) {
      subscriber.sendData();
    }
  } else {
    subscriber.sendData();
  }

  dispatchResult.unsubscribe();
};

const getTargetFormId = (fields: Array<DynamicFormField>, entityId?: unknown) => {
  const formField = findFieldRecursiveSearch(fields ?? [], (field) => field.id === entityId);
  if (formField && formField.dynamicProperties) {
    return formField.dynamicProperties.targetModuleFormId as number;
  }
};

// Modifies the 'archived' filter to support the archived filter format
const modifyOptionsArchivedFilter = (options: OdinDataRetrievalOptions) => {
  if (options.fieldFilters && options.fieldFilters.archived && options.fieldFilters.archived.value === true) {
    // When checked then filter needs to be set to 'all' to include both 'archived' and 'non-archived' options
    options.fieldFilters.archived.value = 'all';
  }
};

// Appends '[Archived]' to the option display value
const addArchivedSuffix = (value: string) => `${value} [Archived]`;

// Check if lookup type is configured for the column field
const hasLookupType = (options?: OdinDataRetrievalOptions) => {
  return Boolean(
    options &&
    options.customProperties &&
    'lookupType' in options.customProperties &&
    options.customProperties.lookupType !== 'NONE'
  );
};
